import React, { useState } from 'react';
import { makeStyles, Typography, CircularProgress } from '@material-ui/core';
import { compose } from 'ramda';
import { reduxForm, InjectedFormProps, Form, FieldArray } from 'redux-form';
import Fields from 'components/Fields';
import { BoxWrapper, Button } from 'components/Base';
import Store from 'store/store';
import { QuestionType } from 'store/modules/Quesionnare/types';
import Questions from './Questions';
import { CustomerType } from 'store/modules/Customers/types';
import api from 'api';
import { Notify, errHelper } from 'utils';
import Router from 'components/Router/Router';

const { useParams } = Router;

const { connect, selectors, actions } = Store;

const useStyles = makeStyles({
  question: {
    display: 'flex',
    height: '69px',
    fontSize: '13px',
    borderBottom: '1px solid #dfdfdf',
    '&:nth-child(odd)': {
      backgroundColor: '#f9f9f9'
    }
  },
  intBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.2rem',
    width: '54px',
    minWidth: '54px',
    backgroundColor: '#7fb847'
  },
  titleBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0 1rem'
  },
  responseBox: {
    display: 'flex',
    alignItems: 'center',
    width: '290px',
    maxWidth: '290px',
    minWidth: '290px',
    borderLeft: '1px solid #dfdfdf',
    padding: '0 1rem',
    justifyContent: 'space-between',

    '& > div': {
      width: '100%'
    }
  },
  select: {
    width: '100%'
  },
  checkboxWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginLeft: 'auto'
  },
  icon: {
    cursor: 'pointer'
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  user: {
    marginLeft: '1rem',
    fontSize: '1.1rem',
    lineHeight: '1.3rem'
  },
  clientNameBox: {
    width: '290px',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 1rem',
    maxWidth: '290px',
    minWidth: '290px',
    alignItems: 'center'
  },
  loader: {
    marginLeft: 8
  }
});

interface Props {
  questions: QuestionType[];
  customers: any;
  isJointed: boolean;
  setIsJoined: (value: boolean) => void;
  userResponses: any;
}

const Questionnare = ({
  handleSubmit,
  customers,
  isJointed,
  setIsJoined,
  userResponses
}: Props & InjectedFormProps) => {
  const { portfolio } = useParams() as any;
  const [isProcessing, setIsProcessing] = useState(false);
  const [downloadingAtrCustomerId, setDownloadingAtrCustomerId] = useState<
    number | null
  >(null);

  const isMoreThanOneCustomer = customers.length > 1;
  const titleToDisplay = isMoreThanOneCustomer
    ? isJointed
      ? customers.find((customer: CustomerType) => customer.is_jointed)
          .display_name
      : ''
    : customers[0].display_name;

  const individualCustomers = customers.filter(
    (customer: CustomerType) => !customer.is_jointed
  );

  const classes = useStyles();

  const downloadQuestionnare = (customerId: number, blank = false) => {
    if (blank) {
      setIsProcessing(true);
    } else {
      setDownloadingAtrCustomerId(customerId);
    }

    const questionnareId: any = userResponses[customerId]?.id;
    if (!questionnareId && !blank) {
      setDownloadingAtrCustomerId(null);
      return Notify.info('Info', 'Please fill out questionnaire');
    }

    const apiCall = blank
      ? api.downloadBlankQuestionnaire({ customerId })
      : api.downloadQuestionnare({
          customerId,
          questionnaireId: userResponses[customerId]?.id,
          questionnaire2Id: null,
          caseId: portfolio,
          images: undefined,
          docType: 'ATR'
        });

    return apiCall
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const customerData = customers.find(
          (c: CustomerType) => c.id === customerId
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `Questionnaire ${customerData ? customerData.display_name : ''}.pdf`
        );
        document.body.appendChild(link);
        link.click();
      })
      .catch((err: any) => {
        errHelper(err);
      })
      .finally(() => {
        if (blank) setIsProcessing(false);
        else setDownloadingAtrCustomerId(null);
      });
  };

  return (
    <BoxWrapper
      title={`Attitude to Risk Questionnaire`}
      headerChildren={
        <div className={classes.header}>
          <Typography className={classes.user} variant="h5">
            {titleToDisplay}
          </Typography>
          {isMoreThanOneCustomer && (
            <div className={classes.checkboxWrapper}>
              <Fields.CheckboxCustom
                isChecked={isJointed}
                label="Joint questionnaire"
                onChange={() => setIsJoined(!isJointed)}
              />
            </div>
          )}
          <div>
            <Button
              style={{
                marginLeft: '1rem'
              }}
              isLoading={isProcessing}
              onClick={() => downloadQuestionnare(customers[0].id, true)}
            >
              Print Blank Form
            </Button>
          </div>
        </div>
      }
    >
      <div className={classes.question} style={{ height: '45px' }}>
        <div className={classes.intBox}></div>
        <div className={classes.titleBox}>QUESTION</div>
        {isMoreThanOneCustomer &&
          !isJointed &&
          individualCustomers.map((customer: CustomerType) => (
            <div key={customer.id} className={classes.clientNameBox}>
              <div>{customer.display_name}</div>
              {userResponses[customer.id]?.id && (
                <div
                  style={{
                    fontWeight: 600,
                    color: '#4469a6',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  onClick={() => downloadQuestionnare(customer.id, false)}
                >
                  Download
                  {downloadingAtrCustomerId === customer.id && (
                    <CircularProgress className={classes.loader} size={16} />
                  )}
                </div>
              )}
            </div>
          ))}
        {isJointed &&
          (() => {
            const jointCustomer = customers.find(
              (customer: CustomerType) => customer.is_jointed
            );
            return (
              jointCustomer &&
              userResponses[jointCustomer.id]?.id && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginRight: '1rem'
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: '#4469a6',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onClick={() =>
                      downloadQuestionnare(jointCustomer.id, false)
                    }
                  >
                    Download
                    {downloadingAtrCustomerId === jointCustomer.id && (
                      <CircularProgress className={classes.loader} size={16} />
                    )}
                  </div>
                </div>
              )
            );
          })()}
      </div>
      <Form onSubmit={handleSubmit}>
        <FieldArray
          name="questions"
          component={Questions}
          rerenderOnEveryChange
          customers={customers}
          isJointed={isJointed}
        />
      </Form>
    </BoxWrapper>
  );
};

export default compose(
  reduxForm({
    form: 'questionnaire'
  }),
  connect(
    (state: any) => ({
      customers: selectors.caseSelectors.caseCustomers(state),
      isJointed: selectors.questionnareSelectors.isJoinedCustomers(state),
      userResponses: selectors.questionnareSelectors.getUserResponses(state)
    }),
    {
      setIsJoined: actions.questionnareActions.setIsJoinedCustomer
    }
  )
)(Questionnare);
